import apiClient from "./axiosInstance"

// --- Individual endpoint response interfaces ---

export interface HashtagBrandStats {
  hashtagBrands: {
    total: number
    withGptAnalysis: number
    withoutGptAnalysis: number
    withPosts: number
    withoutPosts: number
    gptAnalysisPercent: number
    postsPercent: number
  }
  lastUpdated: string
}

export interface RunpodWorkerStats {
  runpodWorkers: {
    active: number
    running: number
    idle: number
  }
  lastUpdated: string
}

export interface HashtagManagerStats {
  hashtagManagers: {
    total: number
    active: number
    completed: number
    activePercent: number
  }
  lastUpdated: string
}

export interface TaggedProfileStats {
  taggedProfiles: {
    postsWithTags: number
  }
  lastUpdated: string | null
}

export interface PostStats {
  instaPosts: {
    totalWithHashtagBrand: number
    withGpuAnalysis: number
    withoutGpuAnalysis: number
    gpuAnalysisPercent: number
  }
  lastUpdated: string | null
}

export interface HashtagPostCounts {
  instaPosts: {
    totalHashtagPosts: number
    hashtagPostsNeedingAnalysis: number
    hashtagPostsAnalyzed: number
  }
  lastUpdated: string | null
}

export interface HashtagAnalysisJobStats {
  job: {
    totalPosts: number
    completed: number
    pending: number
    waiting: number
  }
  lastUpdated: string | null
}

export interface ProductEmbeddingJobStats {
  productEmbeddingJob: {
    totalPosts: number
    completed: number
    pending: number
    waiting: number
  }
  lastUpdated: string | null
}

export interface InfluencerScrapeStats {
  influencerScrape: {
    total: number
    completed: number
    waiting: number
  }
  lastUpdated: string | null
}

export interface InstaPostCountStats {
  instaPostCountStats: {
    totalPosts: number
    hashtagBrandPosts: number
    gpuAnalyzedPosts: number
  }
  lastUpdated: string | null
}

export interface InstaPostMediaStats {
  instaPostMediaStats: {
    savedPosts: number
    unsavedPosts: number
    noGpuAnalyzedPosts: number
  }
  lastUpdated: string | null
}

// --- Composed flow data (frontend birleştirme) ---

export interface HashtagPipelineFlowData {
  hashtagManagers: {
    total: number
    active: number
    completed: number
    activePercent: number
  }
  hashtagPostCounts: {
    totalHashtagPosts: number
    hashtagPostsNeedingAnalysis: number
    hashtagPostsAnalyzed: number
  }
  hashtagAnalysisJob: {
    totalPosts: number
    completed: number
    pending: number
    waiting: number
  }
  productEmbeddingJob: {
    totalPosts: number
    completed: number
    waiting: number
  }
  influencerScrape: {
    total: number
    completed: number
    waiting: number
  }
  hashtagBrands: {
    total: number
    withGptAnalysis: number
    withoutGptAnalysis: number
    withPosts: number
    withoutPosts: number
    gptAnalysisPercent: number
    postsPercent: number
  }
  runpodWorkers: {
    active: number
    running: number
    idle: number
  }
  taggedProfiles: {
    postsWithTags: number
  }
  instaPostStats: {
    totalPosts: number
    savedPosts: number
    unsavedPosts: number
    hashtagBrandPosts: number
    gpuAnalyzedPosts: number
    noGpuAnalyzedPosts: number
  }
  lastUpdated: string
}

export interface RunpodPod {
  id: string
  name: string
  uptimeSeconds: number
  gpuCount: number
}

export interface RunpodPodsResponse {
  pods: RunpodPod[]
  totalPods: number
  lastUpdated: string
}

// --- API fonksiyonları ---

// Hashtag brand istatistikleri
export const TAgetHashtagBrandStats = async (): Promise<HashtagBrandStats> => {
  const response = await apiClient.get("/admin/hashtag-pipeline/hashtag-brand-stats", {
    timeout: 30000,
  })
  if (response.status === 200) {
    return response.data
  } else {
    throw new Error("Hashtag brand istatistikleri alınamadı")
  }
}

// RunPod worker istatistikleri
export const TAgetRunpodWorkerStats = async (): Promise<RunpodWorkerStats> => {
  const response = await apiClient.get("/admin/hashtag-pipeline/runpod-worker-stats", {
    timeout: 30000,
  })
  if (response.status === 200) {
    return response.data
  } else {
    throw new Error("RunPod worker istatistikleri alınamadı")
  }
}

// Hashtag manager istatistikleri
export const TAgetHashtagManagerStats = async (): Promise<HashtagManagerStats> => {
  const response = await apiClient.get("/admin/hashtag-pipeline/hashtag-manager-stats", {
    timeout: 30000,
  })
  if (response.status === 200) {
    return response.data
  } else {
    throw new Error("Hashtag manager istatistikleri alınamadı")
  }
}

// Tagged profile istatistikleri (cache'den)
export const TAgetTaggedProfileStats = async (): Promise<TaggedProfileStats> => {
  const response = await apiClient.get("/admin/hashtag-pipeline/tagged-profile-stats", {
    timeout: 30000,
  })
  if (response.status === 200) {
    return response.data
  } else {
    throw new Error("Tagged profile istatistikleri alınamadı")
  }
}

// Post istatistikleri (cache'den)
export const TAgetHashtagPipelinePostStats = async (): Promise<PostStats> => {
  const response = await apiClient.get("/admin/hashtag-pipeline/post-stats", {
    timeout: 30000,
  })
  if (response.status === 200) {
    return response.data
  } else {
    throw new Error("Post istatistikleri alınamadı")
  }
}

// Hashtag post sayıları (cache'den)
export const TAgetHashtagPostCounts = async (): Promise<HashtagPostCounts> => {
  const response = await apiClient.get("/admin/hashtag-pipeline/hashtag-post-counts", {
    timeout: 30000,
  })
  if (response.status === 200) {
    return response.data
  } else {
    throw new Error("Hashtag post sayıları alınamadı")
  }
}

// Hashtag analiz job istatistikleri (cache'den)
export const TAgetHashtagAnalysisJobStats = async (): Promise<HashtagAnalysisJobStats> => {
  const response = await apiClient.get("/admin/hashtag-pipeline/hashtag-analysis-job-stats", {
    timeout: 30000,
  })
  if (response.status === 200) {
    return response.data
  } else {
    throw new Error("Hashtag analiz job istatistikleri alınamadı")
  }
}

// Product embedding job istatistikleri (cache'den)
export const TAgetProductEmbeddingJobStats = async (): Promise<ProductEmbeddingJobStats> => {
  const response = await apiClient.get("/admin/hashtag-pipeline/product-embedding-stats", {
    timeout: 30000,
  })
  if (response.status === 200) {
    return response.data
  } else {
    throw new Error("Product embedding istatistikleri alınamadı")
  }
}

// Influencer scrape istatistikleri (cache'den)
export const TAgetInfluencerScrapeStats = async (): Promise<InfluencerScrapeStats> => {
  const response = await apiClient.get("/admin/hashtag-pipeline/influencer-scrape-stats", {
    timeout: 30000,
  })
  if (response.status === 200) {
    return response.data
  } else {
    throw new Error("Influencer scrape istatistikleri alınamadı")
  }
}

// Insta post sayı istatistikleri (cache'den)
export const TAgetInstaPostCountStats = async (): Promise<InstaPostCountStats> => {
  const response = await apiClient.get("/admin/hashtag-pipeline/insta-post-count-stats", {
    timeout: 30000,
  })
  if (response.status === 200) {
    return response.data
  } else {
    throw new Error("Insta post sayı istatistikleri alınamadı")
  }
}

// Insta post medya istatistikleri (cache'den)
export const TAgetInstaPostMediaStats = async (): Promise<InstaPostMediaStats> => {
  const response = await apiClient.get("/admin/hashtag-pipeline/insta-post-media-stats", {
    timeout: 30000,
  })
  if (response.status === 200) {
    return response.data
  } else {
    throw new Error("Insta post medya istatistikleri alınamadı")
  }
}

// Tüm endpoint'lerden veri çekip HashtagPipelineFlowData oluştur
export const TAgetComposedPipelineFlow = async (): Promise<HashtagPipelineFlowData> => {
  const [
    brandStats,
    workerStats,
    managerStats,
    taggedStats,
    postCounts,
    analysisJob,
    productEmbedding,
    influencerScrape,
    postCountStats,
    postMediaStats,
  ] = await Promise.all([
    TAgetHashtagBrandStats(),
    TAgetRunpodWorkerStats(),
    TAgetHashtagManagerStats(),
    TAgetTaggedProfileStats(),
    TAgetHashtagPostCounts(),
    TAgetHashtagAnalysisJobStats(),
    TAgetProductEmbeddingJobStats(),
    TAgetInfluencerScrapeStats(),
    TAgetInstaPostCountStats(),
    TAgetInstaPostMediaStats(),
  ])

  return {
    hashtagManagers: managerStats.hashtagManagers,
    hashtagBrands: brandStats.hashtagBrands,
    runpodWorkers: workerStats.runpodWorkers,
    taggedProfiles: taggedStats.taggedProfiles,
    hashtagPostCounts: postCounts.instaPosts,
    hashtagAnalysisJob: analysisJob.job,
    productEmbeddingJob: {
      totalPosts: productEmbedding.productEmbeddingJob.totalPosts,
      completed: productEmbedding.productEmbeddingJob.completed,
      waiting: productEmbedding.productEmbeddingJob.waiting,
    },
    influencerScrape: influencerScrape.influencerScrape,
    instaPostStats: {
      totalPosts: postCountStats.instaPostCountStats.totalPosts,
      savedPosts: postMediaStats.instaPostMediaStats.savedPosts,
      unsavedPosts: postMediaStats.instaPostMediaStats.unsavedPosts,
      hashtagBrandPosts: postCountStats.instaPostCountStats.hashtagBrandPosts,
      gpuAnalyzedPosts: postCountStats.instaPostCountStats.gpuAnalyzedPosts,
      noGpuAnalyzedPosts: postMediaStats.instaPostMediaStats.noGpuAnalyzedPosts,
    },
    lastUpdated: new Date().toISOString(),
  }
}

// Aktif RunPod podlarını getir
export const TAgetActiveRunpodPods = async (): Promise<RunpodPodsResponse> => {
  const response = await apiClient.get("/admin/hashtag-pipeline/runpod-pods", {
    timeout: 15000,
  })
  if (response.status === 200) {
    return response.data
  } else {
    throw new Error("RunPod podları alınamadı")
  }
}

// Tek pod terminate et
export const TAterminateRunpodPod = async (podId: string) => {
  const response = await apiClient.delete(`/admin/hashtag-pipeline/runpod-pods/${podId}`, {
    timeout: 15000,
  })
  if (response.status === 200) {
    return response.data
  } else {
    throw new Error("Pod terminate edilemedi")
  }
}

// Tüm podları terminate et
export const TAterminateAllRunpodPods = async () => {
  const response = await apiClient.delete("/admin/hashtag-pipeline/runpod-pods", {
    timeout: 30000,
  })
  if (response.status === 200) {
    return response.data
  } else {
    throw new Error("Podlar terminate edilemedi")
  }
}
